import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency, formatDate } from "@/lib/api";
import { Plus, Search, Download, ChevronLeft, ChevronRight, Eye, Edit2, Trash2, X, AlertTriangle } from "lucide-react";

function AssetModal({ asset, categories, onClose, onSave, onDelete }) {
  const [editing, setEditing] = useState(!asset);
  const [form, setForm] = useState(asset || {
    tag: "", description: "", category_id: "", entity_id: "", acquired_date: "",
    cost: 0, accumulated_depreciation: 0, residual_value: 0, useful_life_months: 60,
    depreciation_method: "STRAIGHT_LINE", location: "", custodian: "", serial_number: "",
    condition: "GOOD", notes: ""
  });
  const [saving, setSaving] = useState(false);
  const [damaging, setDamaging] = useState(false);
  const { activeEntity } = useApp();

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, entity_id: form.entity_id || activeEntity?.id };
      if (asset) {
        const { data } = await api.put(`/assets/${asset.id}`, payload);
        onSave(data);
      } else {
        const { data } = await api.post("/assets", payload);
        onSave(data);
      }
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Error saving asset");
    } finally {
      setSaving(false);
    }
  };

  const handleDamage = async () => {
    const notes = prompt("Describe the damage (this will trigger a write-off disposal):");
    if (!notes) return;
    if (!window.confirm(`Mark ${asset.tag} as DAMAGED BEYOND REPAIR?\n\nThis will:\n- Remove the asset from the register\n- Post a write-off journal for the full carrying amount (R${asset.carrying_amount?.toLocaleString()})\n- Record a loss on disposal\n\nThis action cannot be undone.`)) return;
    setDamaging(true);
    try {
      const { data } = await api.post(`/assets/${asset.id}/damage`, { notes, date: new Date().toISOString().substring(0, 10) });
      alert(`Asset written off.\nLoss: R${Math.abs(data.loss).toLocaleString()}\nJournal: ${data.journal.reference}`);
      onSave();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || "Error");
    } finally {
      setDamaging(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="asset-modal">
      <div className="bg-white rounded-xl border border-[#E8E2D5] shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[#E8E2D5] flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-[#0B1020]">
            {asset ? (editing ? "Edit Asset" : "Asset Details") : "New Asset"}
          </h2>
          <div className="flex items-center gap-2">
            {asset && !editing && asset.status === "ACTIVE" && (
              <>
                <button onClick={() => setEditing(true)} className="p-2 rounded-lg hover:bg-[#FAF8F4]" data-testid="edit-asset-btn">
                  <Edit2 size={16} />
                </button>
                <button onClick={handleDamage} disabled={damaging} className="p-2 rounded-lg hover:bg-[#991B1B]/10 text-[#991B1B]" title="Mark as Damaged Beyond Repair" data-testid="damage-asset-btn">
                  <AlertTriangle size={16} />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#FAF8F4]" data-testid="close-modal-btn">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {editing ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Asset Tag</label>
                  <input className="stratum-input w-full" value={form.tag} onChange={(e) => setForm({...form, tag: e.target.value})} data-testid="asset-tag-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Category</label>
                  <select className="stratum-input w-full" value={form.category_id} onChange={(e) => setForm({...form, category_id: e.target.value})} data-testid="asset-category-select">
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-[#6B7280] mb-1">Description</label>
                <input className="stratum-input w-full" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} data-testid="asset-description-input" />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Cost (R)</label>
                  <input type="number" className="stratum-input w-full font-mono" value={form.cost} onChange={(e) => setForm({...form, cost: parseFloat(e.target.value)||0})} data-testid="asset-cost-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Accum Dep (R)</label>
                  <input type="number" className="stratum-input w-full font-mono" value={form.accumulated_depreciation} onChange={(e) => setForm({...form, accumulated_depreciation: parseFloat(e.target.value)||0})} data-testid="asset-accum-dep-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Residual (R)</label>
                  <input type="number" className="stratum-input w-full font-mono" value={form.residual_value} onChange={(e) => setForm({...form, residual_value: parseFloat(e.target.value)||0})} data-testid="asset-residual-input" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Acquired Date</label>
                  <input type="date" className="stratum-input w-full" value={form.acquired_date} onChange={(e) => setForm({...form, acquired_date: e.target.value})} data-testid="asset-date-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Useful Life (months)</label>
                  <input type="number" className="stratum-input w-full font-mono" value={form.useful_life_months} onChange={(e) => setForm({...form, useful_life_months: parseInt(e.target.value)||0})} data-testid="asset-life-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Method</label>
                  <select className="stratum-input w-full" value={form.depreciation_method} onChange={(e) => setForm({...form, depreciation_method: e.target.value})} data-testid="asset-method-select">
                    <option value="STRAIGHT_LINE">Straight-line</option>
                    <option value="DIMINISHING">Diminishing balance</option>
                    <option value="UOP">Units of production</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Location</label>
                  <input className="stratum-input w-full" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})} data-testid="asset-location-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Custodian</label>
                  <input className="stratum-input w-full" value={form.custodian} onChange={(e) => setForm({...form, custodian: e.target.value})} data-testid="asset-custodian-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[#6B7280] mb-1">Condition</label>
                  <select className="stratum-input w-full" value={form.condition} onChange={(e) => setForm({...form, condition: e.target.value})} data-testid="asset-condition-select">
                    <option value="NEW">New</option>
                    <option value="EXCELLENT">Excellent</option>
                    <option value="GOOD">Good</option>
                    <option value="FAIR">Fair</option>
                    <option value="POOR">Poor</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={onClose} className="px-4 py-2 rounded-lg border border-[#E8E2D5] text-sm hover:bg-[#FAF8F4]" data-testid="cancel-asset-btn">Cancel</button>
                <button onClick={handleSave} disabled={saving} className="stratum-btn-primary disabled:opacity-50" data-testid="save-asset-btn">
                  {saving ? "Saving..." : "Save Asset"}
                </button>
              </div>
            </>
          ) : (
            <div className="space-y-4">
              {/* In-Use Toggle */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#FAF8F4] border border-[#E8E2D5]">
                <div>
                  <span className="text-sm font-medium text-[#0B1020]">Asset In Use</span>
                  <p className="text-xs text-[#6B7280]">Mark whether this asset is currently in active use</p>
                </div>
                <button
                  onClick={async () => {
                    const newVal = !(asset?.in_use !== false);
                    try {
                      await api.put(`/assets/${asset.id}/in-use`, { in_use: newVal });
                      onSave();
                    } catch (e) { alert(e.response?.data?.detail || "Error"); }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${asset?.in_use !== false ? "bg-[#065F46] text-white" : "bg-[#991B1B]/10 text-[#991B1B] border border-[#991B1B]/30"}`}
                  data-testid="toggle-in-use-btn"
                >
                  {asset?.in_use !== false ? "IN USE" : "NOT IN USE"}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                {[
                  ["Tag", asset?.tag],
                  ["Category", categories.find(c => c.id === asset?.category_id)?.name],
                  ["Description", asset?.description],
                  ["Status", asset?.status],
                  ["Cost", formatCurrency(asset?.cost)],
                  ["Accum Depreciation", formatCurrency(asset?.accumulated_depreciation)],
                  ["Carrying Amount", formatCurrency(asset?.carrying_amount)],
                  ["Residual Value", formatCurrency(asset?.residual_value)],
                  ["Monthly Dep.", formatCurrency(asset?.monthly_depreciation)],
                  ["Useful Life", `${asset?.useful_life_months} months`],
                  ["Method", asset?.depreciation_method?.replace("_", " ")],
                  ["Acquired", formatDate(asset?.acquired_date)],
                  ["Location", asset?.location],
                  ["Custodian", asset?.custodian],
                  ["Condition", asset?.condition],
                  ["Serial No.", asset?.serial_number],
                ].map(([label, val]) => (
                  <div key={label}>
                    <span className="text-xs text-[#6B7280] uppercase tracking-wider">{label}</span>
                    <p className="text-sm font-medium text-[#1A1A1A] mt-0.5 font-mono tabular-nums">{val || "-"}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AssetsPage() {
  const { activeEntity, categories, getCategoryName } = useApp();
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const pageSize = 25;

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, search, sort_by: "tag", sort_dir: "asc" };
      if (activeEntity) params.entity_id = activeEntity.id;
      if (catFilter) params.category_id = catFilter;
      const { data } = await api.get("/assets", { params });
      setAssets(data.assets);
      setTotal(data.total);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [activeEntity, page, search, catFilter]);

  useEffect(() => { loadAssets(); }, [loadAssets]);

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4" data-testid="assets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Asset Register</h1>
          <p className="text-sm text-[#6B7280] mt-1">{total} assets{activeEntity ? ` in ${activeEntity.name}` : ""}</p>
        </div>
        <button onClick={() => { setSelectedAsset(null); setShowModal(true); }} className="stratum-btn-primary flex items-center gap-2" data-testid="add-asset-btn">
          <Plus size={16} /> Add Asset
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <input
            className="stratum-input w-full pl-9"
            placeholder="Search by tag, description, location..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            data-testid="asset-search-input"
          />
        </div>
        <select className="stratum-input" value={catFilter} onChange={(e) => { setCatFilter(e.target.value); setPage(1); }} data-testid="asset-category-filter">
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="stratum-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full stratum-table" data-testid="asset-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Description</th>
                <th>Category</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Accum Dep</th>
                <th className="text-right">Carrying</th>
                <th>Location</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">Loading...</td></tr>
              ) : assets.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 text-[#6B7280]">No assets found</td></tr>
              ) : assets.map((a) => (
                <tr key={a.id} className="cursor-pointer" onClick={() => { setSelectedAsset(a); setShowModal(true); }} data-testid={`asset-row-${a.tag}`}>
                  <td className="font-mono text-sm font-medium text-[#0B1020]">{a.tag}</td>
                  <td className="max-w-[200px] truncate">{a.description}</td>
                  <td className="text-xs">{getCategoryName(a.category_id)}</td>
                  <td className="text-right font-mono tabular-nums">{formatCurrency(a.cost)}</td>
                  <td className="text-right font-mono tabular-nums text-[#991B1B]">({formatCurrency(Math.abs(a.accumulated_depreciation))})</td>
                  <td className="text-right font-mono tabular-nums font-medium">{formatCurrency(a.carrying_amount)}</td>
                  <td className="text-xs text-[#6B7280]">{a.location}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      a.status === "ACTIVE" ? "bg-[#065F46]/10 text-[#065F46]" :
                      a.status === "DISPOSED" ? "bg-[#991B1B]/10 text-[#991B1B]" :
                      "bg-[#6B7280]/10 text-[#6B7280]"
                    }`}>{a.status}</span>
                  </td>
                  <td>
                    <button className="p-1 rounded hover:bg-[#FAF8F4]" data-testid={`view-asset-${a.tag}`}>
                      <Eye size={14} className="text-[#6B7280]" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#E8E2D5]">
            <span className="text-sm text-[#6B7280]">Page {page} of {totalPages} ({total} assets)</span>
            <div className="flex gap-2">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30 hover:bg-[#FAF8F4]" data-testid="prev-page-btn">
                <ChevronLeft size={16} />
              </button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} className="p-2 rounded-lg border border-[#E8E2D5] disabled:opacity-30 hover:bg-[#FAF8F4]" data-testid="next-page-btn">
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <AssetModal
          asset={selectedAsset}
          categories={categories}
          onClose={() => { setShowModal(false); setSelectedAsset(null); }}
          onSave={() => loadAssets()}
        />
      )}
    </div>
  );
}
